<template>
    <div class="pay-config">
        <div class="page-header">
            <h2 class="page-title">支付配置</h2>
            <p class="page-desc">配置微信支付等支付参数</p>
        </div>

        <div class="config-container">
            <el-card class="config-card" shadow="hover">
                <template #header>
                    <div class="card-header-title">
                        <el-icon>
                            <Wallet />
                        </el-icon>
                        <span>支付类型</span>
                    </div>
                </template>

                <el-form label-width="120px">
                    <el-form-item label="支付类型" required>
                        <el-select v-model="payType" placeholder="请选择支付类型" style="width: 300px"
                            @change="onPayTypeChange">
                            <el-option label="微信支付" value="wechat" />
                            <el-option label="易宝支付" value="yeepay" disabled />
                            <el-option label="随行付" value="suixingfu" disabled />
                        </el-select>
                        <div class="form-tip">其他支付类型即将开放，敬请期待</div>
                    </el-form-item>
                </el-form>
            </el-card>

            <!-- 微信支付配置 -->
            <el-card class="config-card" shadow="hover" v-if="payType === 'wechat'">
                <template #header>
                    <div class="card-header">
                        <div class="card-header-title">
                            <el-icon>
                                <CreditCard />
                            </el-icon>
                            <span>微信支付配置</span>
                        </div>
                        <div class="card-header-actions">
                            <el-button type="primary" link @click="goToPayTest" size="small">
                                <el-icon>
                                    <Connection />
                                </el-icon> 测试支付
                            </el-button>
                            <el-button type="primary" link @click="openPayDialog" size="small">
                                <el-icon>
                                    <Edit />
                                </el-icon> 编辑
                            </el-button>
                        </div>
                    </div>
                </template>
                <div class="config-content">
                    <div class="config-item">
                        <span class="config-label">商户号</span>
                        <span class="config-value">{{ payConfig.mchid || '未配置' }}</span>
                    </div>
                    <div class="config-item">
                        <span class="config-label">API密钥</span>
                        <span class="config-value">
                            <span v-if="payConfig.api_key">●●●●●●●●</span>
                            <span v-else>未配置</span>
                        </span>
                    </div>
                    <div class="config-item">
                        <span class="config-label">APIv3密钥</span>
                        <span class="config-value">
                            <span v-if="payConfig.api_v3_key">●●●●●●●●</span>
                            <span v-else>未配置</span>
                        </span>
                    </div>
                    <div class="config-item">
                        <span class="config-label">商户证书</span>
                        <span class="config-value">{{ payConfig.cert_files && Object.keys(payConfig.cert_files).length >
                            0 ? '已配置' :
                            '未配置' }}</span>
                    </div>
                    <div class="config-item">
                        <span class="config-label">支付模式</span>
                        <span class="config-value">{{ payConfig.pay_mode === 'production' ? '生产模式' : '沙箱模式' }}</span>
                    </div>
                </div>
            </el-card>

            <!-- 特约商户进件 -->
            <el-card class="config-card" shadow="hover" v-if="payType === 'wechat'">
                <template #header>
                    <div class="card-header">
                        <div class="card-header-title">
                            <el-icon>
                                <DocumentAdd />
                            </el-icon>
                            <span>特约商户进件</span>
                        </div>
                        <div class="card-header-actions">
                            <el-button v-if="latestApply && latestApply.status === 1" type="primary" link
                                @click="queryApplyStatus" :loading="querying" size="small">
                                <el-icon>
                                    <Refresh />
                                </el-icon> 刷新状态
                            </el-button>
                            <el-button v-if="latestApply && latestApply.status === 0" type="primary" link
                                @click="openApplyDialog" size="small">
                                <el-icon>
                                    <Edit />
                                </el-icon> 编辑草稿
                            </el-button>
                            <template v-else-if="latestApply && latestApply.status === 3">
                                <el-button type="primary" link @click="openApplyDialog" size="small">
                                    <el-icon>
                                        <Edit />
                                    </el-icon> 修改申请
                                </el-button>
                                <!-- <el-button type="danger" link @click="cancelApply" size="small">
                                    取消申请
                                </el-button> -->
                            </template>
                            <template v-else-if="!latestApply || latestApply.status === 5">
                                <el-button type="primary" link @click="openApplyDialog" size="small">
                                    <el-icon>
                                        <Plus />
                                    </el-icon> 申请进件
                                </el-button>
                            </template>
                        </div>
                    </div>
                </template>
                <div class="config-content">
                    <div v-if="latestApply" class="apply-status">
                        <el-alert :title="statusTitle" :type="statusType" :closable="false" show-icon>
                            <template #default>
                                <div class="status-detail">
                                    <p v-if="latestApply.merchant_name">商户名称：{{ latestApply.merchant_name }}</p>
                                    <p v-if="latestApply.sub_mchid">子商户号：{{ latestApply.sub_mchid }}</p>
                                    <p v-if="latestApply.applyment_id">申请单号：{{ latestApply.applyment_id }}</p>
                                    <p v-if="latestApply.reject_reason" class="reject-reason">驳回原因：{{
                                        latestApply.reject_reason }}</p>
                                    <p
                                        v-if="latestApply.sign_url && shouldShowSignButton(latestApply.wx_applyment_state)">
                                        <el-link type="primary"
                                            @click="showSignQrCode(latestApply.sign_url)">点击前往签约</el-link>
                                    </p>
                                    <p v-if="latestApply.wx_applyment_state === 'APPLYMENT_STATE_FINISHED'"
                                        class="success-tip">
                                        <el-tag type="success">入驻已完成！</el-tag>
                                    </p>
                                </div>
                            </template>
                        </el-alert>
                        <!-- <div v-if="latestApply.status === 0 || latestApply.status === 3" class="apply-actions">
                            <el-button type="danger" link @click="cancelApply">取消申请</el-button>
                        </div> -->
                    </div>
                    <div v-else class="no-apply">
                        <el-empty description="暂无进件申请，点击上方按钮申请" :image-size="80" />
                    </div>
                </div>
            </el-card>
        </div>

        <!-- 微信支付配置弹窗 -->
        <el-dialog v-model="payDialogVisible" title="编辑微信支付配置" width="650px" destroy-on-close>
            <el-form :model="payForm" label-width="110px">
                <el-form-item label="商户号" required>
                    <el-input v-model="payForm.mchid" placeholder="请输入微信支付商户号" />
                </el-form-item>
                <el-form-item label="API密钥" required>
                    <el-input v-model="payForm.api_key" type="password" placeholder="请输入API密钥（32位）" show-password />
                    <div class="form-tip">修改时需要重新输入，留空表示保持不变</div>
                </el-form-item>
                <el-form-item label="APIv3密钥">
                    <el-input v-model="payForm.api_v3_key" type="password" placeholder="请输入APIv3密钥（选填）" show-password />
                </el-form-item>
                <el-form-item label="商户证书">
                    <el-upload :file-list="certFiles" action="#" :limit="2" accept=".pem" :auto-upload="false"
                        :on-change="handleCertChange" :on-remove="handleCertRemove">
                        <el-button type="primary" link>
                            <el-icon>
                                <Upload />
                            </el-icon> 上传证书
                        </el-button>
                        <template #tip>
                            <div class="upload-tip">支持 .pem 格式，最多上传2个文件（apiclient_cert.pem 和 apiclient_key.pem）</div>
                        </template>
                    </el-upload>
                    <div v-if="Object.keys(certData).length > 0" class="cert-list">
                        <div v-for="(url, name) in certData" :key="name" class="cert-item">
                            <span>{{ name }}</span>
                            <el-tag size="small" type="success">已上传</el-tag>
                        </div>
                    </div>
                </el-form-item>
                <el-form-item label="支付模式">
                    <el-radio-group v-model="payForm.pay_mode">
                        <el-radio label="sandbox">沙箱模式（测试）</el-radio>
                        <el-radio label="production">生产模式（正式）</el-radio>
                    </el-radio-group>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="payDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="savePayConfig" :loading="saving">确 定</el-button>
            </template>
        </el-dialog>

        <!-- 签约二维码弹窗 -->
        <el-dialog v-model="signQrDialogVisible" title="扫码签约" width="400px" center>
            <div style="text-align: center;">
                <div v-if="signQrUrl" class="qr-container">
                    <img :src="signQrUrl" alt="签约二维码" class="qr-image" />
                    <p class="qr-tip">请使用超级管理员的微信扫码完成签约</p>
                    <el-button type="primary" link @click="copySignUrl" class="copy-link">复制签约链接</el-button>
                </div>
                <div v-else class="qr-loading">
                    <el-icon class="is-loading">
                        <Loading />
                    </el-icon>
                    <span>加载中...</span>
                </div>
            </div>
            <template #footer>
                <el-button @click="signQrDialogVisible = false">关闭</el-button>
            </template>
        </el-dialog>

        <!-- ========== 进件申请弹窗 - 完整版 ========== -->
        <el-dialog v-model="applyDialogVisible" title="特约商户进件申请" width="950px" destroy-on-close @close="resetApplyForm"
            :close-on-click-modal="false" :close-on-press-escape="false">
            <el-form :model="applyForm" :rules="applyRules" ref="applyFormRef" label-width="150px">

                <!-- ========== 1. 主体信息 ========== -->
                <el-divider content-position="left">主体信息</el-divider>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="主体类型" prop="subject_type">
                            <el-select v-model="applyForm.subject_type" placeholder="请选择主体类型" style="width: 100%">
                                <el-option label="企业" value="SUBJECT_TYPE_ENTERPRISE" />
                                <el-option label="个体工商户" value="SUBJECT_TYPE_INDIVIDUAL" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="商户全称" prop="merchant_name">
                            <el-input v-model="applyForm.merchant_name" placeholder="与营业执照一致" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="商户简称" prop="merchant_shortname">
                            <el-input v-model="applyForm.merchant_shortname" placeholder="将展示给用户" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="营业执照号" prop="license_number">
                            <el-input v-model="applyForm.license_number" placeholder="统一社会信用代码" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="法人姓名" prop="legal_person">
                            <el-input v-model="applyForm.legal_person" placeholder="与身份证一致" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="营业执照有效期开始" prop="license_period_begin">
                            <el-date-picker v-model="applyForm.license_period_begin" type="date" placeholder="开始日期"
                                value-format="YYYY-MM-DD" style="width: 100%" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="营业执照有效期结束" prop="license_period_end">
                            <el-date-picker v-model="applyForm.license_period_end_date" type="date" placeholder="结束日期"
                                value-format="YYYY-MM-DD" style="width: 100%" :disabled="applyForm.license_long_term" />
                            <div style="margin-top: 8px;">
                                <el-checkbox v-model="applyForm.license_long_term">长期有效</el-checkbox>
                            </div>
                            <div class="form-tip">勾选"长期有效"或选择具体日期</div>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="营业执照地址" prop="company_address">
                    <el-input v-model="applyForm.company_address" placeholder="与营业执照上的地址一致" />
                </el-form-item>
                <!-- 营业执照 -->
                <el-form-item label="营业执照" prop="business_license_media_id">
                    <div class="upload-wrapper" v-if="!licenseFileList.length">
                        <el-upload :file-list="licenseFileList" :limit="1" accept="image/jpeg,image/png,image/bmp"
                            :auto-upload="false" :on-change="(file) => handleImageChange(file, 'license')"
                            list-type="picture-card">
                            <el-icon>
                                <Plus />
                            </el-icon>
                        </el-upload>
                        <div class="upload-tip">请上传营业执照（JPG/BMP/PNG，≤5M）</div>
                    </div>
                    <div v-else class="uploaded-preview">
                        <div class="preview-image">
                            <img :src="licenseFileList[0].url" class="preview-img" />
                            <div class="preview-actions">
                                <el-button type="danger" size="small"
                                    @click="handleImageRemove('license')">删除</el-button>
                                <el-button type="primary" size="small"
                                    @click="reUploadImage('license')">重新上传</el-button>
                            </div>
                        </div>
                    </div>
                </el-form-item>

                <!-- ========== 2. 法人/经营者身份信息 ========== -->
                <el-divider content-position="left">法人/经营者身份信息</el-divider>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="身份证姓名" prop="id_card_name">
                            <el-input v-model="applyForm.id_card_name" placeholder="与身份证一致" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="身份证号码" prop="id_card_number">
                            <el-input v-model="applyForm.id_card_number" placeholder="18位身份证号" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="身份证地址" prop="id_card_address">
                    <el-input v-model="applyForm.id_card_address" placeholder="与身份证上的地址一致" />
                </el-form-item>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="身份证有效期开始" prop="card_period_begin">
                            <el-date-picker v-model="applyForm.card_period_begin" type="date" placeholder="开始日期"
                                value-format="YYYY-MM-DD" style="width: 100%" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="身份证有效期结束" prop="card_period_end">
                            <el-date-picker v-model="applyForm.card_period_end_date" type="date" placeholder="结束日期"
                                value-format="YYYY-MM-DD" style="width: 100%" :disabled="applyForm.card_long_term" />
                            <div style="margin-top: 8px;">
                                <el-checkbox v-model="applyForm.card_long_term">长期有效</el-checkbox>
                            </div>
                            <div class="form-tip">勾选"长期有效"或选择具体日期</div>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="是否为最终受益人" prop="owner">
                    <el-switch v-model="applyForm.owner" active-text="是" inactive-text="否" />
                    <div class="form-tip">通常为"是"，表示法人/经营者为最终受益人</div>
                </el-form-item>
                <!-- 身份证照片 -->
                <el-form-item label="身份证照片" prop="id_card_images">
                    <div class="id-card-uploads">
                        <!-- 人像面 -->
                        <div class="upload-item" v-if="!idCardFrontFileList.length">
                            <div class="upload-wrapper">
                                <el-upload :file-list="idCardFrontFileList" :limit="1"
                                    accept="image/jpeg,image/png,image/bmp" :auto-upload="false"
                                    :on-change="(file) => handleImageChange(file, 'idCardFront')"
                                    list-type="picture-card">
                                    <el-icon>
                                        <Plus />
                                    </el-icon>
                                </el-upload>
                                <span class="upload-label">人像面</span>
                            </div>
                        </div>
                        <div v-else class="uploaded-item">
                            <div class="preview-image">
                                <img :src="idCardFrontFileList[0].url" class="preview-img" />
                                <div class="preview-actions">
                                    <el-button type="danger" size="small"
                                        @click="handleImageRemove('idCardFront')">删除</el-button>
                                    <el-button type="primary" size="small"
                                        @click="reUploadImage('idCardFront')">重新上传</el-button>
                                </div>
                                <span class="upload-label">人像面</span>
                            </div>
                        </div>

                        <!-- 国徽面 -->
                        <div class="upload-item" style="margin-left: 20px;" v-if="!idCardBackFileList.length">
                            <div class="upload-wrapper">
                                <el-upload :file-list="idCardBackFileList" :limit="1"
                                    accept="image/jpeg,image/png,image/bmp" :auto-upload="false"
                                    :on-change="(file) => handleImageChange(file, 'idCardBack')"
                                    list-type="picture-card">
                                    <el-icon>
                                        <Plus />
                                    </el-icon>
                                </el-upload>
                                <span class="upload-label">国徽面</span>
                            </div>
                        </div>
                        <div v-else class="uploaded-item">
                            <div class="preview-image">
                                <img :src="idCardBackFileList[0].url" class="preview-img" />
                                <div class="preview-actions">
                                    <el-button type="danger" size="small"
                                        @click="handleImageRemove('idCardBack')">删除</el-button>
                                    <el-button type="primary" size="small"
                                        @click="reUploadImage('idCardBack')">重新上传</el-button>
                                </div>
                                <span class="upload-label">国徽面</span>
                            </div>
                        </div>
                    </div>
                </el-form-item>

                <!-- ========== 3. 经营信息 ========== -->
                <el-divider content-position="left">经营信息</el-divider>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="客服电话" prop="service_phone">
                            <el-input v-model="applyForm.service_phone" placeholder="座机或手机号" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="经营场景" prop="sales_scenes_type">
                            <el-select v-model="applyForm.sales_scenes_type" placeholder="请选择经营场景" style="width: 100%">
                                <el-option label="线下门店" value="SALES_SCENES_STORE" />
                                <el-option label="小程序" value="SALES_SCENES_MINI_PROGRAM" />
                                <el-option label="公众号" value="SALES_SCENES_MP" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 线下门店场景 -->
                <template v-if="applyForm.sales_scenes_type === 'SALES_SCENES_STORE'">
                    <el-form-item label="门店名称" prop="biz_store_name">
                        <el-input v-model="applyForm.biz_store_name" placeholder="如：大郎烧饼" />
                    </el-form-item>
                    <el-form-item label="经营地址详情" prop="biz_store_address">
                        <el-input v-model="applyForm.biz_store_address" placeholder="如：南山区xx大厦x层xxxx室" />
                    </el-form-item>
                    <el-form-item label="门店门头照片" prop="store_entrance_pic">
                        <div class="upload-wrapper">
                            <el-upload :file-list="storeEntranceFileList" :limit="1"
                                accept="image/jpeg,image/png,image/bmp" :auto-upload="false"
                                :on-change="(file) => handleImageChange(file, 'storeEntrance')"
                                :on-remove="() => handleImageRemove('storeEntrance')" list-type="picture-card">
                                <el-icon>
                                    <Plus />
                                </el-icon>
                            </el-upload>
                            <div class="upload-tip">请上传门店门头照片</div>
                        </div>
                    </el-form-item>
                    <el-form-item label="门店内部照片" prop="indoor_pic">
                        <div class="upload-wrapper">
                            <el-upload :file-list="indoorFileList" :limit="1" accept="image/jpeg,image/png,image/bmp"
                                :auto-upload="false" :on-change="(file) => handleImageChange(file, 'indoor')"
                                :on-remove="() => handleImageRemove('indoor')" list-type="picture-card">
                                <el-icon>
                                    <Plus />
                                </el-icon>
                            </el-upload>
                            <div class="upload-tip">请上传门店内部照片</div>
                        </div>
                    </el-form-item>
                    <el-form-item label="商家AppID" prop="biz_sub_appid">
                        <el-input v-model="applyForm.biz_sub_appid" placeholder="已认证的服务号/小程序/APP的AppID" />
                        <div class="form-tip">线下门店场景建议填写，用于开通支付权限</div>
                    </el-form-item>
                </template>

                <!-- 小程序场景 -->
                <template v-if="applyForm.sales_scenes_type === 'SALES_SCENES_MINI_PROGRAM'">
                    <el-form-item label="小程序AppID" prop="mini_program_sub_appid">
                        <el-input v-model="applyForm.mini_program_sub_appid" placeholder="请填写已认证的小程序AppID" />
                    </el-form-item>
                    <el-form-item label="小程序截图" prop="mini_program_pics">
                        <div class="upload-wrapper">
                            <el-upload :file-list="miniProgramPicsFileList" :limit="5"
                                accept="image/jpeg,image/png,image/bmp" :auto-upload="false"
                                :on-change="(file) => handleImageChange(file, 'miniProgramPics')"
                                :on-remove="(file) => handleImageRemove('miniProgramPics', file)"
                                list-type="picture-card" multiple>
                                <el-icon>
                                    <Plus />
                                </el-icon>
                            </el-upload>
                            <div class="upload-tip">最多上传5张，请提供展示商品/服务的页面截图</div>
                        </div>
                    </el-form-item>
                </template>

                <!-- 公众号场景 -->
                <template v-if="applyForm.sales_scenes_type === 'SALES_SCENES_MP'">
                    <el-form-item label="公众号AppID" prop="mp_sub_appid">
                        <el-input v-model="applyForm.mp_sub_appid" placeholder="请填写已认证的服务号或公众号AppID" />
                    </el-form-item>
                    <el-form-item label="公众号页面截图" prop="mp_pics">
                        <div class="upload-wrapper">
                            <el-upload :file-list="mpPicsFileList" :limit="5" accept="image/jpeg,image/png,image/bmp"
                                :auto-upload="false" :on-change="(file) => handleImageChange(file, 'mpPics')"
                                :on-remove="() => handleImageRemove('mpPics')" list-type="picture-card" multiple>
                                <el-icon>
                                    <Plus />
                                </el-icon>
                            </el-upload>
                            <div class="upload-tip">最多上传5张，请提供展示商品/服务的页面截图</div>
                        </div>
                    </el-form-item>
                </template>

                <!-- ========== 4. 行业资质 ========== -->
                <el-divider content-position="left">行业资质</el-divider>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="所属行业" prop="industry_category_id" required>
                            <el-cascader v-model="selectedIndustry" :options="industryOptions" :props="{
                                value: 'value',
                                label: 'label',
                                children: 'children',
                                checkStrictly: false,
                                emitPath: true
                            }" placeholder="请选择所属行业" style="width: 100%" @change="handleIndustryChange" clearable
                                filterable />
                            <div class="form-tip" v-if="selectedIndustryInfo">
                                结算费率：{{ selectedIndustryInfo.rate_desc }}，入账周期：{{ selectedIndustryInfo.billing_cycle }}
                            </div>
                        </el-form-item>

                        <template v-if="needQualifications">
                            <el-form-item label="特殊资质" prop="qualifications">
                                <div class="upload-wrapper">
                                    <el-upload :file-list="qualificationFileList" :limit="5"
                                        accept="image/jpeg,image/png,image/bmp" :auto-upload="false"
                                        :on-change="(file) => handleImageChange(file, 'qualification')"
                                        :on-remove="(file) => handleImageRemove('qualification', file)"
                                        list-type="picture-card" multiple>
                                        <el-icon>
                                            <Plus />
                                        </el-icon>
                                    </el-upload>
                                    <div class="upload-tip">最多上传5张特殊资质照片</div>
                                </div>
                                <div class="form-tip" v-if="qualificationsGuide" style="white-space: pre-line;">{{
                                    qualificationsGuide }}</div>
                            </el-form-item>
                        </template>
                    </el-col>
                </el-row>

                <!-- 5. 结算账户 -->
                <el-divider content-position="left">结算账户</el-divider>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="账户类型" prop="bank_account_type">
                            <el-select v-model="applyForm.bank_account_type" placeholder="请选择账户类型" style="width: 100%"
                                @change="onAccountTypeChange">
                                <el-option label="对公银行账户" value="BANK_ACCOUNT_TYPE_CORPORATE" />
                                <el-option label="个人银行卡" value="BANK_ACCOUNT_TYPE_PERSONAL" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="开户名称" prop="account_name">
                            <el-input v-model="applyForm.account_name"
                                :placeholder="applyForm.bank_account_type === 'BANK_ACCOUNT_TYPE_CORPORATE' ? '对公填公司全称' : '个人填法人姓名'" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="开户银行" prop="account_bank">
                            <el-select v-model="applyForm.selected_bank_code" placeholder="请选择开户银行" filterable remote
                                :remote-method="searchBanks" :loading="bankLoading" style="width: 100%"
                                @change="onBankChange" @visible-change="onBankVisibleChange" clearable>
                                <el-option v-for="bank in bankList" :key="bank.bank_alias_code" :label="bank.bank_alias"
                                    :value="bank.bank_alias_code">
                                    <div style="display: flex; justify-content: space-between; align-items: center;">
                                        <span>{{ bank.bank_alias }}</span>
                                        <el-tag v-if="bank.need_bank_branch" size="small" type="warning">需填支行</el-tag>
                                        <el-tag v-else size="small" type="success">直连银行</el-tag>
                                    </div>
                                </el-option>
                            </el-select>
                            <div class="form-tip">支持搜索，绿色标签表示无需填写支行</div>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="银行账号" prop="account_number">
                            <el-input v-model="applyForm.account_number" placeholder="请输入银行账号" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 支行信息（仅当银行需要填写支行时显示） -->
                <template v-if="needBankBranch">
                    <el-divider content-position="left">支行信息</el-divider>
                    <el-row :gutter="20">
                        <el-col :span="12">
                            <el-form-item label="所在省份" prop="province_code" required>
                                <el-select v-model="applyForm.province_code" placeholder="请选择省份" filterable
                                    :loading="provinceLoading" style="width: 100%" @change="onProvinceChange" clearable>
                                    <el-option v-for="province in provinceList" :key="province.province_code"
                                        :label="province.province_name" :value="province.province_code">
                                    </el-option>
                                </el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :span="12">
                            <el-form-item label="所在城市" prop="city_code" required>
                                <el-select v-model="applyForm.city_code" placeholder="请先选择省份" filterable
                                    :loading="cityLoading" :disabled="!applyForm.province_code" style="width: 100%"
                                    @change="onCityChange" clearable>
                                    <el-option v-for="city in cityList" :key="city.city_code" :label="city.city_name"
                                        :value="city.city_code">
                                    </el-option>
                                </el-select>
                            </el-form-item>
                        </el-col>
                    </el-row>
                    <el-row :gutter="20">
                        <el-col :span="12">
                            <el-form-item label="开户支行" prop="bank_branch_name" required>
                                <el-select v-model="applyForm.bank_branch_name" placeholder="请选择开户支行" filterable remote
                                    :remote-method="searchBranches" :loading="branchLoading" style="width: 100%"
                                    @change="onBranchChange" clearable>
                                    <el-option v-for="branch in branchList" :key="branch.bank_branch_id"
                                        :label="branch.bank_branch_name" :value="branch.bank_branch_name">
                                        <div>
                                            <span>{{ branch.bank_branch_name }}</span>
                                            <span style="float: right; color: #8492a6; font-size: 12px">
                                                联行号: {{ branch.bank_branch_id }}
                                            </span>
                                        </div>
                                    </el-option>
                                </el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :span="12">
                            <el-form-item label="联行号" prop="bank_branch_id">
                                <el-input v-model="applyForm.bank_branch_id" placeholder="选择支行后自动填充" disabled />
                            </el-form-item>
                        </el-col>
                    </el-row>
                </template>

                <!-- ========== 6. 超级管理员 ========== -->
                <el-divider content-position="left">超级管理员（用于签约和管理）</el-divider>
                <el-form-item label="管理员类型" prop="contact_type">
                    <el-radio-group v-model="applyForm.contact_type">
                        <el-radio value="LEGAL">法定代表人/经营者</el-radio>
                        <el-radio value="SUPER">经办人</el-radio>
                    </el-radio-group>
                </el-form-item>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="管理员姓名" prop="contact_name">
                            <el-input v-model="applyForm.contact_name" placeholder="通常为法人姓名" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="管理员手机" prop="contact_mobile">
                            <el-input v-model="applyForm.contact_mobile" placeholder="11位手机号" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="管理员邮箱" prop="contact_email">
                            <el-input v-model="applyForm.contact_email" placeholder="用于接收重要通知" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 经办人证件信息（当选择经办人时显示） -->
                <template v-if="applyForm.contact_type === 'SUPER'">
                    <el-form-item label="证件类型" prop="contact_id_doc_type">
                        <el-select v-model="applyForm.contact_id_doc_type" placeholder="请选择证件类型" style="width: 100%">
                            <el-option label="中国大陆居民-身份证" value="IDENTIFICATION_TYPE_IDCARD" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="证件号码" prop="contact_id_number">
                        <el-input v-model="applyForm.contact_id_number" placeholder="请输入身份证号码" />
                    </el-form-item>
                    <el-form-item label="证件照片（人像面）" prop="contact_id_doc_copy">
                        <div class="upload-wrapper" v-if="!contactIdDocCopyFileList.length">
                            <el-upload :file-list="contactIdDocCopyFileList" :limit="1"
                                accept="image/jpeg,image/png,image/bmp" :auto-upload="false"
                                :on-change="(file) => handleImageChange(file, 'contactIdDocCopy')"
                                list-type="picture-card">
                                <el-icon>
                                    <Plus />
                                </el-icon>
                            </el-upload>
                        </div>
                        <div v-else class="uploaded-preview">
                            <div class="preview-image">
                                <img :src="contactIdDocCopyFileList[0].url" class="preview-img" />
                                <div class="preview-actions">
                                    <el-button type="danger" size="small"
                                        @click="handleImageRemove('contactIdDocCopy')">删除</el-button>
                                    <el-button type="primary" size="small"
                                        @click="reUploadImage('contactIdDocCopy')">重新上传</el-button>
                                </div>
                            </div>
                        </div>
                    </el-form-item>

                    <el-form-item label="证件照片（国徽面）" prop="contact_id_doc_copy_back">
                        <div class="upload-wrapper" v-if="!contactIdDocCopyBackFileList.length">
                            <el-upload :file-list="contactIdDocCopyBackFileList" :limit="1"
                                accept="image/jpeg,image/png,image/bmp" :auto-upload="false"
                                :on-change="(file) => handleImageChange(file, 'contactIdDocCopyBack')"
                                list-type="picture-card">
                                <el-icon>
                                    <Plus />
                                </el-icon>
                            </el-upload>
                        </div>
                        <div v-else class="uploaded-preview">
                            <div class="preview-image">
                                <img :src="contactIdDocCopyBackFileList[0].url" class="preview-img" />
                                <div class="preview-actions">
                                    <el-button type="danger" size="small"
                                        @click="handleImageRemove('contactIdDocCopyBack')">删除</el-button>
                                    <el-button type="primary" size="small"
                                        @click="reUploadImage('contactIdDocCopyBack')">重新上传</el-button>
                                </div>
                            </div>
                        </div>
                    </el-form-item>
                    <el-row :gutter="20">
                        <el-col :span="12">
                            <el-form-item label="证件有效期开始" prop="contact_period_begin">
                                <el-date-picker v-model="applyForm.contact_period_begin" type="date" placeholder="开始日期"
                                    value-format="YYYY-MM-DD" style="width: 100%" />
                            </el-form-item>
                        </el-col>
                        <el-col :span="12">
                            <el-form-item label="证件有效期结束" prop="contact_period_end">
                                <el-date-picker v-model="applyForm.contact_period_end_date" type="date"
                                    placeholder="结束日期" value-format="YYYY-MM-DD" style="width: 100%"
                                    :disabled="applyForm.contact_long_term" />
                                <div style="margin-top: 8px;">
                                    <el-checkbox v-model="applyForm.contact_long_term">长期有效</el-checkbox>
                                </div>
                                <div class="form-tip">勾选"长期有效"或选择具体日期</div>
                            </el-form-item>
                        </el-col>
                    </el-row>
                </template>
            </el-form>
            <template #footer>
                <el-button @click="applyDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitApply" :loading="submittingApply">提交申请</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElLoading, ElMessageBox } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { getUploadSignature, uploadCertToLocal } from '@/utils/uploadUtils'
import { Wallet, CreditCard, DocumentAdd, Edit, Upload, Plus, Refresh, Connection, Loading } from '@element-plus/icons-vue'

export default {
    name: 'PayConfig',
    components: { Wallet, CreditCard, DocumentAdd, Edit, Upload, Plus, Refresh, Connection, Loading },
    setup() {
        const router = useRouter()
        const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
        const storeId = ref(userInfo.id || 0)
        const agentId = ref(userInfo.agent_id || 0)

        const payType = ref('wechat')
        const payConfig = ref({})
        const saving = ref(false)
        const payDialogVisible = ref(false)
        const certFiles = ref([])
        const certData = ref({})

        const payForm = reactive({
            mchid: '',
            api_key: '',
            api_v3_key: '',
            pay_mode: 'sandbox'
        })

        const latestApply = ref(null)
        const querying = ref(false)
        const submittingApply = ref(false)
        const applyDialogVisible = ref(false)
        const applyFormRef = ref(null)
        const signQrDialogVisible = ref(false)
        const signQrUrl = ref('')

        // 省份城市相关
        const provinceList = ref([])
        const provinceLoading = ref(false)
        const cityList = ref([])
        const cityLoading = ref(false)

        // 银行相关
        const bankList = ref([])
        const bankLoading = ref(false)
        const needBankBranch = ref(false)
        const selectedBankInfo = ref(null)

        // 支行相关
        const branchList = ref([])
        const branchLoading = ref(false)

        // 行业相关
        const industryOptions = ref([])
        const selectedIndustry = ref([])
        const selectedIndustryInfo = ref(null)
        const needQualifications = ref(false)
        const qualificationsGuide = ref('')
        const qualificationFileList = ref([])

        // 文件列表
        const licenseFileList = ref([])
        const idCardFrontFileList = ref([])
        const idCardBackFileList = ref([])
        const storeEntranceFileList = ref([])
        const indoorFileList = ref([])
        const miniProgramPicsFileList = ref([])
        const mpPicsFileList = ref([])
        const contactIdDocCopyFileList = ref([])
        const contactIdDocCopyBackFileList = ref([])

        const applyForm = reactive({
            subject_type: 'SUBJECT_TYPE_INDIVIDUAL',
            merchant_name: '',
            merchant_shortname: '',
            license_number: '',
            legal_person: '',
            license_period_begin: '',
            license_period_end: '长期',
            license_period_end_date: '',
            license_long_term: true,
            company_address: '',
            business_license_media_id: '',
            business_license_url: '',
            id_card_name: '',
            id_card_number: '',
            id_card_address: '',
            card_period_begin: '',
            card_period_end: '长期',
            card_period_end_date: '',
            card_long_term: false,
            owner: true,
            id_card_front_media_id: '',
            id_card_front_url: '',
            id_card_back_media_id: '',
            id_card_back_url: '',
            service_phone: '',
            sales_scenes_type: 'SALES_SCENES_MINI_PROGRAM',
            biz_store_name: '',
            biz_store_address: '',
            store_entrance_pic: '',
            indoor_pic: '',
            biz_sub_appid: '',
            mini_program_sub_appid: '',
            mini_program_pics: '[]',
            mp_sub_appid: '',
            mp_pics: '[]',
            account_bank_code: '',
            selected_bank_code: '',
            selected_bank_name: '',
            province_code: '',
            city_code: '',
            bank_branch_name: '',
            bank_branch_id: '',
            industry_category_id: 0,
            industry_category_name: '',
            settlement_rule_id: '719',
            qualification_type: '餐饮',
            qualifications: '[]',
            bank_account_type: 'BANK_ACCOUNT_TYPE_PERSONAL',
            account_name: '',
            account_bank: '',
            account_number: '',
            contact_type: 'LEGAL',
            contact_name: '',
            contact_mobile: '',
            contact_email: '',
            contact_id_number: '',
            contact_id_doc_type: 'IDENTIFICATION_TYPE_IDCARD',
            contact_id_doc_copy: '',
            contact_id_doc_copy_back: '',
            contact_period_begin: '',
            contact_period_end: '长期',
            contact_period_end_date: '',
            contact_long_term: false
        })

        const applyRules = {
            subject_type: [{ required: true, message: '请选择主体类型', trigger: 'change' }],
            merchant_name: [{ required: true, message: '请输入商户全称', trigger: 'blur' }],
            merchant_shortname: [{ required: true, message: '请输入商户简称', trigger: 'blur' }],
            license_number: [{ required: true, message: '请输入营业执照号', trigger: 'blur' }],
            legal_person: [{ required: true, message: '请输入法人姓名', trigger: 'blur' }],
            license_period_begin: [{ required: true, message: '请选择营业执照有效期开始', trigger: 'change' }],
            license_period_end: [{ required: true, message: '请输入营业执照有效期结束', trigger: 'blur' }],
            company_address: [{ required: true, message: '请输入营业执照地址', trigger: 'blur' }],
            business_license_media_id: [{ required: true, message: '请上传营业执照', trigger: 'change' }],
            id_card_name: [{ required: true, message: '请输入身份证姓名', trigger: 'blur' }],
            id_card_number: [{ required: true, message: '请输入身份证号码', trigger: 'blur' }],
            id_card_address: [{ required: true, message: '请输入身份证地址', trigger: 'blur' }],
            card_period_begin: [{ required: true, message: '请选择身份证有效期开始', trigger: 'change' }],
            card_period_end: [{ required: true, message: '请输入身份证有效期结束', trigger: 'blur' }],
            service_phone: [{ required: true, message: '请输入客服电话', trigger: 'blur' }],
            sales_scenes_type: [{ required: true, message: '请选择经营场景', trigger: 'change' }],
            bank_account_type: [{ required: true, message: '请选择账户类型', trigger: 'change' }],
            account_name: [{ required: true, message: '请输入开户名称', trigger: 'blur' }],
            account_bank: [{ required: true, message: '请输入开户银行', trigger: 'blur' }],
            account_number: [{ required: true, message: '请输入银行账号', trigger: 'blur' }],
            contact_type: [{ required: true, message: '请选择管理员类型', trigger: 'change' }],
            contact_name: [{ required: true, message: '请输入管理员姓名', trigger: 'blur' }],
            industry_category_id: [{ required: true, message: '请选择所属行业', trigger: 'change' }],
            contact_mobile: [
                { required: true, message: '请输入管理员手机', trigger: 'blur' },
                { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
            ],
            contact_email: [{ required: true, message: '请输入管理员邮箱', trigger: 'blur' }],
            contact_id_doc_type: [{
                required: true, message: '请选择证件类型', trigger: 'change',
                validator: (rule, value, callback) => {
                    if (applyForm.contact_type === 'SUPER' && !value) callback(new Error('请选择证件类型'))
                    else callback()
                }
            }],
            contact_id_doc_copy: [{
                required: true, message: '请上传证件正面照片', trigger: 'change',
                validator: (rule, value, callback) => {
                    if (applyForm.contact_type === 'SUPER' && !value) callback(new Error('请上传证件正面照片'))
                    else callback()
                }
            }],
            contact_id_doc_copy_back: [{
                required: true, message: '请上传证件反面照片', trigger: 'change',
                validator: (rule, value, callback) => {
                    if (applyForm.contact_type === 'SUPER' && !value) callback(new Error('请上传证件反面照片'))
                    else callback()
                }
            }],
            contact_period_begin: [{
                required: true, message: '请选择证件有效期开始', trigger: 'change',
                validator: (rule, value, callback) => {
                    if (applyForm.contact_type === 'SUPER' && !value) callback(new Error('请选择证件有效期开始'))
                    else callback()
                }
            }],
            contact_period_end: [{
                required: true, message: '请输入证件有效期结束', trigger: 'blur',
                validator: (rule, value, callback) => {
                    if (applyForm.contact_type === 'SUPER' && !value) callback(new Error('请输入证件有效期结束'))
                    else callback()
                }
            }]
        }

        const onPayTypeChange = () => {
            if (payType.value !== 'wechat') {
                ElMessage.info('其他支付类型暂未开放，敬请期待')
                payType.value = 'wechat'
            }
        }

        const fetchPayConfig = async () => {
            const loading = ElLoading.service({ text: '加载中...' })
            try {
                const res = await axios.get(`${config.BASE_URL}/shop/api/config_api.php`, {
                    params: { agent_id: agentId.value, store_id: storeId.value, config_key: 'wechat_pay' }
                })
                if (res.data.code === 200 && res.data.data) {
                    payConfig.value = res.data.data
                    if (payConfig.value.cert_files) certData.value = payConfig.value.cert_files
                }
            } catch (err) {
                console.error(err)
                ElMessage.error('获取配置失败')
            } finally {
                loading.close()
            }
        }

        const fetchLatestApply = async () => {
            try {
                const res = await axios.get(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, {
                    params: { action: 'latest', store_id: storeId.value }
                })
                if (res.data.code === 200) {
                    latestApply.value = res.data.data || null
                }
            } catch (error) {
                console.error('获取进件状态失败:', error)
            }
        }

        const fetchIndustryList = async () => {
            try {
                let apiUrl = ''
                if (applyForm.subject_type === 'SUBJECT_TYPE_INDIVIDUAL') {
                    apiUrl = `${config.BASE_URL}/shop/api/industry_api.php`
                } else {
                    apiUrl = `${config.BASE_URL}/shop/api/industry_api_qy.php`
                }
                const res = await axios.get(apiUrl, { params: { action: 'list' } })
                if (res.data.errorcode === 0) {
                    industryOptions.value = res.data.data.big_industry_list.map(item => ({
                        value: item.big_industry_info.big_industry_id,
                        label: item.big_industry_info.big_industry_name,
                        children: item.industry_category_list.map(cat => ({
                            value: cat.industry_category_info.category_id,
                            label: cat.industry_category_info.category_name,
                            category_info: cat.industry_category_info,
                            settlement_info: cat.settlement_rule_info
                        }))
                    }))
                }
            } catch (error) {
                console.error('获取行业列表失败:', error)
            }
        }

        const handleIndustryChange = (value) => {
            if (!value || value.length === 0) {
                selectedIndustryInfo.value = null
                needQualifications.value = false
                qualificationsGuide.value = ''
                return
            }
            const categoryId = value[value.length - 1]
            for (const big of industryOptions.value) {
                const category = big.children?.find(cat => cat.value === categoryId)
                if (category) {
                    const catInfo = category.category_info
                    const settlementInfo = category.settlement_info
                    selectedIndustryInfo.value = {
                        category_id: catInfo.category_id,
                        category_name: catInfo.category_name,
                        settlement_rule_id: settlementInfo.settlement_rule_id,
                        rate_desc: settlementInfo.rate_desc,
                        billing_cycle: settlementInfo.billing_cycle_desc,
                        qualifications_required: catInfo.qualifications_type === 1,
                        qualifications_guide: catInfo.qualifications_guide
                    }
                    needQualifications.value = catInfo.qualifications_type === 1
                    qualificationsGuide.value = catInfo.qualifications_guide || ''
                    applyForm.industry_category_id = catInfo.category_id
                    applyForm.industry_category_name = catInfo.category_name
                    applyForm.settlement_rule_id = String(settlementInfo.settlement_rule_id)
                    applyForm.qualification_type = catInfo.category_name
                    break
                }
            }
        }

        const openPayDialog = () => {
            Object.assign(payForm, {
                mchid: payConfig.value.mchid || '',
                api_key: payConfig.value.api_key || '',
                api_v3_key: payConfig.value.api_v3_key || '',
                pay_mode: payConfig.value.pay_mode || 'sandbox'
            })
            if (payConfig.value.cert_files) {
                certData.value = { ...payConfig.value.cert_files }
                certFiles.value = Object.keys(certData.value).map((name, index) => ({
                    uid: Date.now() + index, name: name, url: certData.value[name]
                }))
            } else {
                certFiles.value = []
                certData.value = {}
            }
            payDialogVisible.value = true
        }

        const handleCertChange = async (file, fileList) => {
            if (!file.raw) return
            if (!file.raw.name.endsWith('.pem')) { ElMessage.error('请上传 .pem 格式的证书文件'); return false }
            if (file.raw.size > 2 * 1024 * 1024) { ElMessage.error('证书文件大小不能超过2MB'); return false }
            try {
                const signature = await getUploadSignature('cert')
                const result = await uploadCertToLocal(file.raw, signature)
                certData.value[file.raw.name] = result.url
                certFiles.value = fileList
                ElMessage.success(`${file.raw.name} 上传成功`)
            } catch (error) {
                console.error('上传失败:', error)
                ElMessage.error(error.message || '上传失败')
            }
        }

        const handleCertRemove = (file) => { delete certData.value[file.name] }

        const savePayConfig = async () => {
            if (!payForm.mchid) { ElMessage.warning('请填写商户号'); return }
            saving.value = true
            const loading = ElLoading.service({ text: '保存中...' })
            try {
                const configValue = { mchid: payForm.mchid, pay_mode: payForm.pay_mode, cert_files: certData.value }
                if (payForm.api_key) configValue.api_key = payForm.api_key
                else if (payConfig.value.api_key) configValue.api_key = payConfig.value.api_key
                else configValue.api_key = ''
                if (payForm.api_v3_key) configValue.api_v3_key = payForm.api_v3_key
                else if (payConfig.value.api_v3_key) configValue.api_v3_key = payConfig.value.api_v3_key
                else configValue.api_v3_key = ''

                const response = await axios.post(`${config.BASE_URL}/shop/api/config_api.php`, {
                    action: 'save', agent_id: agentId.value, store_id: storeId.value,
                    config_key: 'wechat_pay', config_value: JSON.stringify(configValue),
                    config_group: 'payment', description: '微信支付配置'
                })
                if (response.data.code === 200) {
                    ElMessage.success('保存成功')
                    payConfig.value = configValue
                    payDialogVisible.value = false
                    fetchPayConfig()
                } else {
                    ElMessage.error(response.data.message || '保存失败')
                }
            } catch (error) {
                console.error('保存失败:', error)
                ElMessage.error('保存失败')
            } finally {
                saving.value = false
                loading.close()
            }
        }

        const goToPayTest = () => { router.push('/pay-test') }

        const queryApplyStatus = async () => {
            querying.value = true
            try {
                const res = await axios.get(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, {
                    params: { action: 'query', store_id: storeId.value }
                })
                if (res.data.code === 200) {
                    ElMessage.success('状态已更新')
                    // 关键：刷新后重新获取最新数据
                    await fetchLatestApply()

                    // 根据本地 status 显示提示
                    const status = latestApply.value?.status
                    if (status === 4) {
                        ElMessage.success('入驻已完成！')
                    } else if (status === 3) {
                        ElMessage.warning('申请被驳回，请查看驳回原因并修改后重新提交')
                    } else if (status === 1) {
                        ElMessage.info('申请正在审核中，请耐心等待')
                    } else if (latestApply.value?.wx_applyment_state === 'APPLYMENT_STATE_TO_BE_SIGNED') {
                        ElMessage.info('待签约，请扫码签约')
                    }
                } else {
                    ElMessage.error(res.data.message || '查询失败')
                }
            } catch (error) {
                console.error('查询失败:', error)
                ElMessage.error('查询失败')
            } finally {
                querying.value = false
            }
        }

        const fetchProvinces = async () => {
            provinceLoading.value = true
            try {
                const res = await axios.get(`${config.BASE_URL}/shop/api/bank_api.php`, {
                    params: { action: 'provinces', agent_id: agentId.value }
                })
                if (res.data.code === 200) provinceList.value = res.data.data || []
            } catch (error) { console.error('获取省份列表失败:', error) }
            finally { provinceLoading.value = false }
        }

        const onProvinceChange = async (provinceCode) => {
            applyForm.city_code = ''
            applyForm.bank_branch_name = ''
            applyForm.bank_branch_id = ''
            cityList.value = []
            branchList.value = []
            if (!provinceCode) return
            cityLoading.value = true
            try {
                const res = await axios.get(`${config.BASE_URL}/shop/api/bank_api.php`, {
                    params: { action: 'cities', agent_id: agentId.value, province_code: provinceCode }
                })
                if (res.data.code === 200) cityList.value = res.data.data || []
            } catch (error) { console.error('获取城市列表失败:', error) }
            finally { cityLoading.value = false }
        }

        const onCityChange = async (cityCode) => {
            applyForm.bank_branch_name = ''
            applyForm.bank_branch_id = ''
            branchList.value = []
            if (!cityCode || !selectedBankInfo.value) return
            await loadBranches('')
        }

        const loadBranches = async (keyword = '', offset = 0) => {
            if (!selectedBankInfo.value || !applyForm.city_code) return
            branchLoading.value = true
            try {
                const res = await axios.get(`${config.BASE_URL}/shop/api/bank_api.php`, {
                    params: {
                        action: 'branches', agent_id: agentId.value,
                        bank_alias_code: selectedBankInfo.value.bank_alias_code,
                        city_code: applyForm.city_code, offset: offset, limit: 100, keyword: keyword
                    }
                })
                if (res.data.code === 200) {
                    const branchData = res.data.data
                    branchList.value = branchData.data || []
                    if (branchData.account_bank) {
                        applyForm.account_bank = branchData.account_bank
                        applyForm.account_bank_code = branchData.account_bank_code
                    }
                }
            } catch (error) { console.error('获取支行列表失败:', error) }
            finally { branchLoading.value = false }
        }

        const searchBranches = async (keyword) => {
            if (!selectedBankInfo.value || !applyForm.city_code) {
                if (keyword) ElMessage.warning('请先选择省份和城市')
                return
            }
            await loadBranches(keyword)
        }

        const onBranchChange = (branchName) => {
            const selected = branchList.value.find(b => b.bank_branch_name === branchName)
            if (selected) applyForm.bank_branch_id = selected.bank_branch_id
        }

        const onAccountTypeChange = () => {
            applyForm.account_bank = ''
            applyForm.account_bank_code = ''
            applyForm.province_code = ''
            applyForm.city_code = ''
            applyForm.bank_branch_name = ''
            applyForm.bank_branch_id = ''
            bankList.value = []
            needBankBranch.value = false
            selectedBankInfo.value = null
            cityList.value = []
            branchList.value = []
            searchBanks('')
        }

        const onBankVisibleChange = (visible) => {
            if (!visible && applyForm.account_bank && bankList.value.length > 0) {
                const exists = bankList.value.some(b => b.account_bank === applyForm.account_bank)
                if (!exists && applyForm.selected_bank_name) searchBanks(applyForm.selected_bank_name)
            }
        }

        const debounce = (fn, delay) => {
            let timer = null
            return function (...args) {
                if (timer) clearTimeout(timer)
                timer = setTimeout(() => { fn.apply(this, args) }, delay)
            }
        }

        const loadBankList = async (keyword = '') => {
            bankLoading.value = true
            try {
                const accountType = applyForm.bank_account_type === 'BANK_ACCOUNT_TYPE_CORPORATE' ? 'corporate' : 'personal'
                const res = await axios.get(`${config.BASE_URL}/shop/api/bank_api.php`, {
                    params: { action: 'banks', agent_id: agentId.value, account_type: accountType, keyword: keyword, offset: 0, limit: 200 }
                })
                if (res.data.code === 200) bankList.value = res.data.data.data || []
            } catch (error) { console.error('获取银行列表失败:', error) }
            finally { bankLoading.value = false }
        }

        const searchBanks = debounce(async (keyword) => { await loadBankList(keyword) }, 300)

        const onBankChange = (bankAliasCode) => {
            if (!bankAliasCode) return
            const selected = bankList.value.find(b => b.bank_alias_code === bankAliasCode)
            if (selected) {
                selectedBankInfo.value = selected
                needBankBranch.value = selected.need_bank_branch === true
                applyForm.account_bank_code = selected.account_bank_code
                applyForm.selected_bank_name = selected.bank_alias
                applyForm.selected_bank_code = bankAliasCode
                applyForm.province_code = ''
                applyForm.city_code = ''
                applyForm.bank_branch_name = ''
                applyForm.bank_branch_id = ''
                cityList.value = []
                branchList.value = []
                if (!needBankBranch.value) {
                    applyForm.account_bank = selected.account_bank  // 修改这里
                    ElMessage.success(`已选择 ${selected.bank_alias}，无需填写支行信息`)
                } else {
                    applyForm.account_bank = '其他银行'
                    ElMessage.info('请选择省份和城市，然后选择支行')
                    fetchProvinces()
                }
            }
        }

        const resetApplyForm = () => {
            // 保存当前的主体类型
            const currentSubjectType = applyForm.subject_type

            Object.assign(applyForm, {
                subject_type: currentSubjectType,  // 使用当前值而不是默认值
                merchant_name: '', merchant_shortname: '', license_number: '', legal_person: '',
                license_period_begin: '', license_period_end: '长期', license_period_end_date: '', license_long_term: true,
                company_address: '', business_license_media_id: '', business_license_url: '',
                id_card_name: '', id_card_number: '', id_card_address: '', card_period_begin: '', card_period_end: '长期',
                card_period_end_date: '', card_long_term: false, owner: true,
                id_card_front_media_id: '', id_card_front_url: '', id_card_back_media_id: '', id_card_back_url: '',
                service_phone: '', sales_scenes_type: 'SALES_SCENES_MINI_PROGRAM',
                biz_store_name: '', biz_store_address: '', store_entrance_pic: '', indoor_pic: '', biz_sub_appid: '',
                mini_program_sub_appid: '', mini_program_pics: '[]', mp_sub_appid: '', mp_pics: '[]',
                account_bank_code: '', selected_bank_code: '', selected_bank_name: '', province_code: '', city_code: '',
                bank_branch_name: '', bank_branch_id: '', industry_category_id: 0, industry_category_name: '',
                settlement_rule_id: '719', qualification_type: '餐饮', qualifications: '[]',
                bank_account_type: 'BANK_ACCOUNT_TYPE_PERSONAL', account_name: '', account_bank: '', account_number: '',
                contact_type: 'LEGAL', contact_name: '', contact_mobile: '', contact_email: '', contact_id_number: '',
                contact_id_doc_type: 'IDENTIFICATION_TYPE_IDCARD', contact_id_doc_copy: '', contact_id_doc_copy_back: '',
                contact_period_begin: '', contact_period_end: '长期', contact_period_end_date: '', contact_long_term: false
            })
            licenseFileList.value = []
            idCardFrontFileList.value = []
            idCardBackFileList.value = []
            storeEntranceFileList.value = []
            indoorFileList.value = []
            miniProgramPicsFileList.value = []
            mpPicsFileList.value = []
            contactIdDocCopyFileList.value = []
            contactIdDocCopyBackFileList.value = []
            qualificationFileList.value = []
            selectedIndustry.value = []
            selectedIndustryInfo.value = null
            needQualifications.value = false
            bankList.value = []
            provinceList.value = []
            cityList.value = []
            branchList.value = []
            needBankBranch.value = false
            selectedBankInfo.value = null
            applyFormRef.value?.clearValidate()
        }

        const handleImageChange = async (file, type) => {
            if (!file.raw) return
            const rawFile = file.raw
            const validTypes = ['image/jpeg', 'image/png', 'image/bmp']
            if (!validTypes.includes(rawFile.type)) { ElMessage.error('只支持 JPG、PNG、BMP 格式'); return false }
            if (rawFile.size > 5 * 1024 * 1024) { ElMessage.error('图片大小不能超过5MB'); return false }

            const formData = new FormData()
            formData.append('file', rawFile)
            formData.append('action', 'upload_image')
            formData.append('store_id', storeId.value)
            formData.append('agent_id', agentId.value)

            const loading = ElLoading.service({ text: '上传中...' })
            try {
                const res = await axios.post(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                })
                if (res.data.code === 200 && res.data.data?.media_id) {
                    const mediaId = res.data.data.media_id
                    const previewUrl = res.data.data.preview_url || ''

                    const typeMap = {
                        license: { mediaField: 'business_license_media_id', urlField: 'business_license_url', fileList: licenseFileList },
                        idCardFront: { mediaField: 'id_card_front_media_id', urlField: 'id_card_front_url', fileList: idCardFrontFileList },
                        idCardBack: { mediaField: 'id_card_back_media_id', urlField: 'id_card_back_url', fileList: idCardBackFileList },
                        storeEntrance: { mediaField: 'store_entrance_pic', fileList: storeEntranceFileList, storeBoth: true },
                        indoor: { mediaField: 'indoor_pic', fileList: indoorFileList, storeBoth: true },
                        contactIdDocCopy: { mediaField: 'contact_id_doc_copy', fileList: contactIdDocCopyFileList, storeBoth: true },
                        contactIdDocCopyBack: { mediaField: 'contact_id_doc_copy_back', fileList: contactIdDocCopyBackFileList, storeBoth: true },
                        miniProgramPics: { mediaField: 'mini_program_pics', fileList: miniProgramPicsFileList, isArray: true, storeBoth: true },
                        mpPics: { mediaField: 'mp_pics', fileList: mpPicsFileList, isArray: true, storeBoth: true },
                        qualification: { mediaField: 'qualifications', fileList: qualificationFileList, isArray: true, storeBoth: true }
                    }

                    const config = typeMap[type]
                    if (config) {
                        if (config.isArray) {
                            let items = []
                            try { items = JSON.parse(applyForm[config.mediaField] || '[]') } catch (e) { items = [] }
                            items.push({ media_id: mediaId, url: previewUrl })
                            applyForm[config.mediaField] = JSON.stringify(items)
                            config.fileList.value.push({ uid: Date.now(), name: rawFile.name, url: previewUrl })
                        } else {
                            applyForm[config.mediaField] = JSON.stringify([{ media_id: mediaId, url: previewUrl }])
                            if (config.urlField) applyForm[config.urlField] = previewUrl
                            config.fileList.value = [{ uid: Date.now(), name: rawFile.name, url: previewUrl }]
                        }
                    }
                    ElMessage.success('上传成功')
                } else {
                    ElMessage.error(res.data.message || '上传失败')
                }
            } catch (error) {
                console.error('上传失败:', error)
                ElMessage.error('上传失败')
            } finally {
                loading.close()
            }
        }

        const handleImageRemove = (type, file) => {
            const typeMap = {
                license: { mediaField: 'business_license_media_id', urlField: 'business_license_url', fileList: licenseFileList },
                idCardFront: { mediaField: 'id_card_front_media_id', urlField: 'id_card_front_url', fileList: idCardFrontFileList },
                idCardBack: { mediaField: 'id_card_back_media_id', urlField: 'id_card_back_url', fileList: idCardBackFileList },
                storeEntrance: { mediaField: 'store_entrance_pic', fileList: storeEntranceFileList, storeBoth: true },
                indoor: { mediaField: 'indoor_pic', fileList: indoorFileList, storeBoth: true },
                contactIdDocCopy: { mediaField: 'contact_id_doc_copy', fileList: contactIdDocCopyFileList, storeBoth: true },
                contactIdDocCopyBack: { mediaField: 'contact_id_doc_copy_back', fileList: contactIdDocCopyBackFileList, storeBoth: true },
                miniProgramPics: { mediaField: 'mini_program_pics', fileList: miniProgramPicsFileList, isArray: true, storeBoth: true },
                mpPics: { mediaField: 'mp_pics', fileList: mpPicsFileList, isArray: true, storeBoth: true },
                qualification: { mediaField: 'qualifications', fileList: qualificationFileList, isArray: true, storeBoth: true }
            }

            const config = typeMap[type]
            if (!config) { console.error('未知的图片类型:', type); return }

            const fileUid = file?.uid
            if (!fileUid) { console.error('无法获取文件 uid:', file); return }

            if (config.isArray) {
                let items = []
                try { items = JSON.parse(applyForm[config.mediaField] || '[]') } catch (e) { items = [] }
                const index = config.fileList.value.findIndex(f => f.uid === fileUid)
                if (index !== -1) {
                    items.splice(index, 1)
                    applyForm[config.mediaField] = JSON.stringify(items)
                    config.fileList.value.splice(index, 1)
                }
            } else if (config.storeBoth) {
                applyForm[config.mediaField] = ''
                config.fileList.value = []
            } else {
                applyForm[config.mediaField] = ''
                if (config.urlField) applyForm[config.urlField] = ''
                config.fileList.value = []
            }
        }

        const reUploadImage = (type) => {
            const typeMap = {
                license: { mediaField: 'business_license_media_id', urlField: 'business_license_url', fileList: licenseFileList },
                idCardFront: { mediaField: 'id_card_front_media_id', urlField: 'id_card_front_url', fileList: idCardFrontFileList },
                idCardBack: { mediaField: 'id_card_back_media_id', urlField: 'id_card_back_url', fileList: idCardBackFileList },
                contactIdDocCopy: { mediaField: 'contact_id_doc_copy', urlField: null, fileList: contactIdDocCopyFileList },
                contactIdDocCopyBack: { mediaField: 'contact_id_doc_copy_back', urlField: null, fileList: contactIdDocCopyBackFileList }
            }
            const config = typeMap[type]
            if (config) {
                applyForm[config.mediaField] = ''
                if (config.urlField) applyForm[config.urlField] = ''
                config.fileList.value = []
            }
        }

        const submitApply = async () => {
            if (!applyFormRef.value) return

            if (needBankBranch.value) {
                if (!applyForm.province_code) { ElMessage.warning('请选择省份'); return }
                if (!applyForm.city_code) { ElMessage.warning('请选择城市'); return }
                if (!applyForm.bank_branch_name) { ElMessage.warning('请选择开户支行'); return }
                if (!applyForm.bank_branch_id) { ElMessage.warning('请选择开户支行'); return }
            }

            await applyFormRef.value.validate(async (valid) => {
                if (!valid) return
                if (!applyForm.business_license_media_id) { ElMessage.warning('请上传营业执照'); return }
                if (!applyForm.id_card_front_media_id || !applyForm.id_card_back_media_id) { ElMessage.warning('请上传身份证正反面'); return }

                submittingApply.value = true
                try {
                    const submitData = { ...applyForm }
                    if (applyForm.license_long_term) submitData.license_period_end = '长期'
                    else submitData.license_period_end = applyForm.license_period_end_date || ''
                    if (applyForm.card_long_term) submitData.card_period_end = '长期'
                    else submitData.card_period_end = applyForm.card_period_end_date || ''
                    if (applyForm.contact_long_term) submitData.contact_period_end = '长期'
                    else submitData.contact_period_end = applyForm.contact_period_end_date || ''

                    const formatImageField = (val) => {
                        if (!val) return ''
                        try {
                            if (typeof val === 'object') return JSON.stringify(val)
                            const parsed = JSON.parse(val)
                            return JSON.stringify(parsed)
                        } catch (e) {
                            return JSON.stringify([{ media_id: val, url: '' }])
                        }
                    }

                    submitData.store_entrance_pic = formatImageField(applyForm.store_entrance_pic)
                    submitData.indoor_pic = formatImageField(applyForm.indoor_pic)
                    submitData.contact_id_doc_copy = formatImageField(applyForm.contact_id_doc_copy)
                    submitData.contact_id_doc_copy_back = formatImageField(applyForm.contact_id_doc_copy_back)
                    submitData.mini_program_pics = formatImageField(applyForm.mini_program_pics)
                    submitData.mp_pics = formatImageField(applyForm.mp_pics)
                    submitData.qualifications = formatImageField(applyForm.qualifications)

                    const draftPayload = { action: 'save_draft', store_id: storeId.value, agent_id: agentId.value, ...submitData }
                    const draftRes = await axios.post(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, draftPayload)
                    if (draftRes.data.code !== 200) { ElMessage.error(draftRes.data.message || '保存草稿失败'); return }

                    const draftId = draftRes.data.id
                    ElMessage.success('草稿已保存，正在提交申请...')

                    const submitPayload = { action: 'submit', store_id: storeId.value, agent_id: agentId.value, draft_id: draftId, ...submitData }
                    const submitRes = await axios.post(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, submitPayload)

                    if (submitRes.data.code === 200) {
                        ElMessage.success('申请已提交，请等待审核')
                        applyDialogVisible.value = false
                        await fetchLatestApply()
                    } else {
                        ElMessage.warning(submitRes.data.message || '提交失败，草稿已保存，可稍后重试')
                        applyDialogVisible.value = false
                        await fetchLatestApply()
                    }
                } catch (error) {
                    console.error('提交失败:', error)
                    ElMessage.error('提交失败，但草稿已保存')
                    await fetchLatestApply()
                } finally {
                    submittingApply.value = false
                }
            })
        }

        const cancelApply = async () => {
            if (!latestApply.value) return
            try {
                await ElMessageBox.confirm('确定取消当前申请吗？', '提示', { type: 'warning' })
                const res = await axios.post(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, {
                    action: 'cancel', store_id: storeId.value, id: latestApply.value.id
                })
                if (res.data.code === 200) { ElMessage.success('已取消'); await fetchLatestApply() }
                else { ElMessage.error(res.data.message || '取消失败') }
            } catch (error) { if (error !== 'cancel') console.error(error) }
        }

        const openApplyDialog = async () => {
            resetApplyForm()
            if (industryOptions.value.length === 0) await fetchIndustryList()
            try {
                const res = await axios.get(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, {
                    params: { action: 'latest', store_id: storeId.value }
                })
                if (res.data.code === 200 && res.data.data) {
                    const draft = res.data.data
                    if (draft.status === 0 || draft.status === 3) {
                        Object.keys(applyForm).forEach(key => {
                            if (draft[key] !== undefined && draft[key] !== null && draft[key] !== '') {
                                if (key === 'owner') applyForm[key] = draft[key] == 1 ? true : false
                                else applyForm[key] = draft[key]
                            }
                        })

                        // 根据草稿中的 subject_type 加载行业列表
                        await fetchIndustryList()

                        if (draft.qualification_type) {
                            for (const big of industryOptions.value) {
                                const category = big.children?.find(cat => cat.label === draft.qualification_type)
                                if (category) {
                                    selectedIndustry.value = [big.value, category.value]
                                    handleIndustryChange(selectedIndustry.value)
                                    break
                                }
                            }
                        }
                        if (draft.store_entrance_pic && draft.store_entrance_pic !== '') {
                            try {
                                let items = JSON.parse(draft.store_entrance_pic)
                                if (!Array.isArray(items)) items = [items]
                                const item = items[0] || {}
                                storeEntranceFileList.value = [{ uid: Date.now(), name: '门头照片', url: item.url || '', media_id: item.media_id || '' }]
                                applyForm.store_entrance_pic = draft.store_entrance_pic
                            } catch (e) { console.error('解析门头照片失败:', e) }
                        }
                        if (draft.indoor_pic && draft.indoor_pic !== '') {
                            try {
                                let items = JSON.parse(draft.indoor_pic)
                                if (!Array.isArray(items)) items = [items]
                                const item = items[0] || {}
                                indoorFileList.value = [{ uid: Date.now(), name: '内部照片', url: item.url || '', media_id: item.media_id || '' }]
                                applyForm.indoor_pic = draft.indoor_pic
                            } catch (e) { console.error('解析内部照片失败:', e) }
                        }
                        if (draft.contact_id_doc_copy && draft.contact_id_doc_copy !== '') {
                            try {
                                let items = JSON.parse(draft.contact_id_doc_copy)
                                if (!Array.isArray(items)) items = [items]
                                const item = items[0] || {}
                                contactIdDocCopyFileList.value = [{ uid: Date.now(), name: '证件正面', url: item.url || '', media_id: item.media_id || '' }]
                                applyForm.contact_id_doc_copy = draft.contact_id_doc_copy
                            } catch (e) { console.error('解析经办人证件正面失败:', e) }
                        }
                        if (draft.contact_id_doc_copy_back && draft.contact_id_doc_copy_back !== '') {
                            try {
                                let items = JSON.parse(draft.contact_id_doc_copy_back)
                                if (!Array.isArray(items)) items = [items]
                                const item = items[0] || {}
                                contactIdDocCopyBackFileList.value = [{ uid: Date.now(), name: '证件反面', url: item.url || '', media_id: item.media_id || '' }]
                                applyForm.contact_id_doc_copy_back = draft.contact_id_doc_copy_back
                            } catch (e) { console.error('解析经办人证件反面失败:', e) }
                        }
                        if (draft.mini_program_pics && draft.mini_program_pics !== '[]') {
                            try {
                                const items = JSON.parse(draft.mini_program_pics)
                                miniProgramPicsFileList.value = items.map((item, index) => ({
                                    uid: Date.now() + index, name: `截图${index + 1}`, url: item.url || '', media_id: item.media_id || ''
                                }))
                            } catch (e) { console.error('解析小程序截图失败:', e) }
                        }
                        if (draft.mp_pics && draft.mp_pics !== '[]') {
                            try {
                                const items = JSON.parse(draft.mp_pics)
                                mpPicsFileList.value = items.map((item, index) => ({
                                    uid: Date.now() + index, name: `截图${index + 1}`, url: item.url || '', media_id: item.media_id || ''
                                }))
                            } catch (e) { console.error('解析公众号截图失败:', e) }
                        }
                        if (draft.qualifications && draft.qualifications !== '[]') {
                            try {
                                const items = JSON.parse(draft.qualifications)
                                if (items.length > 0 && typeof items[0] === 'object' && items[0].media_id) {
                                    qualificationFileList.value = items.map((item, index) => ({
                                        uid: Date.now() + index, name: `资质${index + 1}`, url: item.url || '', media_id: item.media_id
                                    }))
                                } else {
                                    qualificationFileList.value = items.map((mediaId, index) => ({
                                        uid: Date.now() + index, name: `资质${index + 1}`, media_id: mediaId, url: ''
                                    }))
                                }
                            } catch (e) { console.error('解析特殊资质失败:', e) }
                        }
                        if (draft.license_period_end === '长期') { applyForm.license_long_term = true; applyForm.license_period_end_date = '' }
                        else { applyForm.license_long_term = false; applyForm.license_period_end_date = draft.license_period_end || '' }
                        if (draft.card_period_end === '长期') { applyForm.card_long_term = true; applyForm.card_period_end_date = '' }
                        else { applyForm.card_long_term = false; applyForm.card_period_end_date = draft.card_period_end || '' }
                        if (draft.contact_period_end === '长期') { applyForm.contact_long_term = true; applyForm.contact_period_end_date = '' }
                        else if (draft.contact_period_end) { applyForm.contact_long_term = false; applyForm.contact_period_end_date = draft.contact_period_end }
                        if (draft.business_license_url) licenseFileList.value = [{ uid: Date.now(), name: '营业执照', url: draft.business_license_url }]
                        if (draft.id_card_front_url) idCardFrontFileList.value = [{ uid: Date.now(), name: '身份证正面', url: draft.id_card_front_url }]
                        if (draft.id_card_back_url) idCardBackFileList.value = [{ uid: Date.now(), name: '身份证反面', url: draft.id_card_back_url }]
                        if (draft.selected_bank_name) {
                            try {
                                bankLoading.value = true
                                const accountType = applyForm.bank_account_type === 'BANK_ACCOUNT_TYPE_CORPORATE' ? 'corporate' : 'personal'

                                // 关键：如果 draft.selected_bank_code 存在，优先用代码搜索
                                let searchKeyword = draft.selected_bank_code || draft.selected_bank_name
                                const searchRes = await axios.get(`${config.BASE_URL}/shop/api/bank_api.php`, {
                                    params: {
                                        action: 'banks',
                                        agent_id: agentId.value,
                                        account_type: accountType,
                                        keyword: searchKeyword,
                                        offset: 0,
                                        limit: 20
                                    }
                                })

                                if (searchRes.data.code === 200) {
                                    const matchedList = searchRes.data.data.data || []
                                    // 优先用代码匹配，其次用名称匹配
                                    let matched = null
                                    if (draft.selected_bank_code) {
                                        matched = matchedList.find(b => b.bank_alias_code === draft.selected_bank_code)
                                    }
                                    if (!matched) {
                                        matched = matchedList.find(b => b.bank_alias === draft.selected_bank_name)
                                    }

                                    if (matched) {
                                        selectedBankInfo.value = matched
                                        needBankBranch.value = matched.need_bank_branch === true
                                        applyForm.account_bank_code = matched.account_bank_code
                                        applyForm.selected_bank_name = matched.bank_alias
                                        applyForm.selected_bank_code = matched.bank_alias_code

                                        // 确保 bankList 中包含这个银行（用于下拉框显示）
                                        if (!bankList.value.some(b => b.bank_alias_code === matched.bank_alias_code)) {
                                            bankList.value.unshift(matched)
                                        }

                                        if (needBankBranch.value) {
                                            applyForm.account_bank = '其他银行'

                                            // 如果需要支行信息且已有数据，继续加载省份城市和支行
                                            if (draft.bank_name) {
                                                // 加载省份列表
                                                if (provinceList.value.length === 0) {
                                                    await fetchProvinces()
                                                }

                                                // 设置省份
                                                if (draft.province_code) {
                                                    applyForm.province_code = parseInt(draft.province_code)
                                                    await onProvinceChange(applyForm.province_code)

                                                    // 设置城市
                                                    if (draft.city_code) {
                                                        await nextTick()
                                                        const cityCodeNum = parseInt(draft.city_code)
                                                        const cityExists = cityList.value.some(c => c.city_code === cityCodeNum)
                                                        if (cityExists) {
                                                            applyForm.city_code = cityCodeNum
                                                            await onCityChange(applyForm.city_code)
                                                            await nextTick()

                                                            // 搜索并匹配支行
                                                            await loadBranches('')
                                                            const branchMatched = branchList.value.find(b => b.bank_branch_name === draft.bank_name)
                                                            if (branchMatched) {
                                                                applyForm.bank_branch_name = branchMatched.bank_branch_name
                                                                applyForm.bank_branch_id = branchMatched.bank_branch_id
                                                            } else if (draft.bank_name) {
                                                                // 如果没匹配到，手动设置支行名称
                                                                applyForm.bank_branch_name = draft.bank_name
                                                                applyForm.bank_branch_id = draft.bank_branch_id || ''
                                                            }
                                                        } else if (draft.city_code) {
                                                            // 城市不在列表中，手动设置
                                                            applyForm.city_code = draft.city_code
                                                            applyForm.bank_branch_name = draft.bank_name
                                                            applyForm.bank_branch_id = draft.bank_branch_id || ''
                                                        }
                                                    }
                                                } else if (draft.bank_name) {
                                                    // 没有省份城市，直接设置支行名称
                                                    applyForm.bank_branch_name = draft.bank_name
                                                    applyForm.bank_branch_id = draft.bank_branch_id || ''
                                                }
                                            }
                                        } else {
                                            // 直连银行
                                            applyForm.account_bank = matched.account_bank  // 修改这里
                                            // 清空支行相关字段
                                            applyForm.province_code = ''
                                            applyForm.city_code = ''
                                            applyForm.bank_branch_name = ''
                                            applyForm.bank_branch_id = ''
                                        }
                                    } else {
                                        // 没匹配到，手动构造一个选项加入 bankList
                                        console.warn('未找到匹配的银行，手动添加:', draft.selected_bank_name)
                                        const fakeBank = {
                                            bank_alias: draft.selected_bank_name,
                                            bank_alias_code: draft.selected_bank_code || `custom_${Date.now()}`,
                                            account_bank: draft.account_bank || draft.selected_bank_name,
                                            account_bank_code: draft.account_bank_code || '',
                                            need_bank_branch: false
                                        }
                                        bankList.value.unshift(fakeBank)
                                        applyForm.selected_bank_code = fakeBank.bank_alias_code
                                        applyForm.selected_bank_name = fakeBank.bank_alias
                                        applyForm.account_bank = draft.account_bank || draft.selected_bank_name
                                        needBankBranch.value = false

                                        // 清空支行相关字段
                                        applyForm.province_code = ''
                                        applyForm.city_code = ''
                                        applyForm.bank_branch_name = ''
                                        applyForm.bank_branch_id = ''
                                    }
                                }
                            } catch (error) {
                                console.error('搜索银行失败:', error)
                                // 出错时手动添加
                                const fakeBank = {
                                    bank_alias: draft.selected_bank_name,
                                    bank_alias_code: draft.selected_bank_code || `custom_${Date.now()}`,
                                    account_bank: draft.account_bank || draft.selected_bank_name,
                                    account_bank_code: draft.account_bank_code || '',
                                    need_bank_branch: false
                                }
                                bankList.value.unshift(fakeBank)
                                applyForm.selected_bank_code = fakeBank.bank_alias_code
                                applyForm.selected_bank_name = fakeBank.bank_alias
                                applyForm.account_bank = draft.account_bank || draft.selected_bank_name
                                needBankBranch.value = false

                                // 清空支行相关字段
                                applyForm.province_code = ''
                                applyForm.city_code = ''
                                applyForm.bank_branch_name = ''
                                applyForm.bank_branch_id = ''
                            } finally {
                                bankLoading.value = false
                            }
                        }
                        if (draft.status === 0) ElMessage.info('已加载上次保存的草稿，可继续编辑')
                        else if (draft.status === 3) ElMessage.warning('上次申请被驳回，请修改后重新提交')
                    }
                }
            } catch (error) { console.error('加载数据失败:', error) }
            applyDialogVisible.value = true
        }

        watch(() => applyForm.subject_type, async (newVal, oldVal) => {
            if (applyDialogVisible.value && oldVal && newVal !== oldVal) {
                await fetchIndustryList()
                selectedIndustry.value = []
                selectedIndustryInfo.value = null
                needQualifications.value = false
                qualificationsGuide.value = ''
                applyForm.industry_category_id = 0
                applyForm.industry_category_name = ''
                applyForm.settlement_rule_id = '719'
                applyForm.qualification_type = ''
                ElMessage.info('主体类型已切换，请重新选择所属行业')
            }
        })

        const showSignQrCode = (url) => {
            if (url) { signQrUrl.value = url; signQrDialogVisible.value = true }
        }

        const copySignUrl = async () => {
            try { await navigator.clipboard.writeText(signQrUrl.value); ElMessage.success('签约链接已复制，请在浏览器中打开') }
            catch (err) { ElMessage.error('复制失败，请手动复制') }
        }

        // 修改 shouldShowSignButton 函数，已签约状态不显示签约按钮：
        const shouldShowSignButton = (wxState) => {
            // 如果本地状态已经是已签约(4)，不显示签约按钮
            if (latestApply.value && latestApply.value.status === 4) {
                return false
            }
            // 需要显示签约按钮的状态
            const needActionStates = [
                'APPLYMENT_STATE_AUDITING',
                'APPLYMENT_STATE_TO_BE_CONFIRMED',
                'APPLYMENT_STATE_TO_BE_SIGNED'
            ]
            return needActionStates.includes(wxState)
        }

        // 将 statusTitle 计算属性修改为：
        const statusTitle = computed(() => {
            if (!latestApply.value) return ''

            // 优先使用本地 status 字段（更准确）
            // status: 0=草稿, 1=审核中, 2=审核通过, 3=审核驳回, 4=已签约, 5=已取消
            const status = latestApply.value.status

            // 本地状态映射
            const localStatusMap = {
                0: '草稿（待提交）',
                1: '审核中',
                2: '审核通过',
                3: '审核驳回',
                4: '已签约',
                5: '已取消'
            }

            // 如果本地状态是已签约(4)或审核通过(2)，直接显示
            if (status === 4) {
                return '当前状态：已签约'
            }
            if (status === 2) {
                return '当前状态：审核通过'
            }
            if (status === 5) {
                return '当前状态：已取消'
            }

            // 其他情况使用微信状态
            if (latestApply.value.wx_applyment_state) {
                const stateTextMap = {
                    'APPLYMENT_STATE_EDITTING': '编辑中',
                    'APPLYMENT_STATE_AUDITING': '审核中',
                    'APPLYMENT_STATE_REJECTED': '审核驳回',
                    'APPLYMENT_STATE_TO_BE_CONFIRMED': '待账户验证',
                    'APPLYMENT_STATE_TO_BE_SIGNED': '待签约',
                    'APPLYMENT_STATE_SIGNING': '开通权限中',
                    'APPLYMENT_STATE_FINISHED': '已完成',
                    'APPLYMENT_STATE_CANCELED': '已作废'
                }
                let text = stateTextMap[latestApply.value.wx_applyment_state] || '未知'
                if (latestApply.value.wx_applyment_state_msg &&
                    latestApply.value.wx_applyment_state !== 'APPLYMENT_STATE_FINISHED') {
                    text += `（${latestApply.value.wx_applyment_state_msg}）`
                }

                // 添加驳回原因显示
                if (latestApply.value.wx_applyment_state === 'APPLYMENT_STATE_REJECTED' &&
                    latestApply.value.wx_result?.audit_detail) {
                    const reasons = latestApply.value.wx_result.audit_detail.map(d => d.reject_reason).join('；')
                    if (reasons) text += `，驳回原因：${reasons}`
                }
                return `当前状态：${text}`
            }

            // 兜底使用本地状态
            return `当前状态：${localStatusMap[status] || '未知'}`
        })

        // 修改 statusType 计算属性：
        const statusType = computed(() => {
            if (!latestApply.value) return 'info'

            const status = latestApply.value.status

            // 优先使用本地状态判断样式
            if (status === 4 || status === 2) return 'success'  // 已签约、审核通过 -> 绿色
            if (status === 3) return 'danger'                   // 审核驳回 -> 红色
            if (status === 1) return 'warning'                  // 审核中 -> 橙色
            if (status === 5) return 'info'                     // 已取消 -> 灰色

            // 其他情况使用微信状态
            const wxState = latestApply.value.wx_applyment_state
            if (wxState === 'APPLYMENT_STATE_FINISHED') return 'success'
            if (wxState === 'APPLYMENT_STATE_REJECTED') return 'danger'
            if (wxState === 'APPLYMENT_STATE_AUDITING') return 'warning'
            if (wxState === 'APPLYMENT_STATE_TO_BE_SIGNED') return 'warning'

            return 'info'
        })

        onMounted(() => {
            fetchPayConfig()
            fetchLatestApply()
            searchBanks('')
        })

        return {
            payType, payConfig, saving, payDialogVisible, certFiles, certData, payForm,
            latestApply, querying, submittingApply, applyDialogVisible, applyForm, applyRules, applyFormRef,
            signQrDialogVisible, signQrUrl, onPayTypeChange, openPayDialog, handleCertChange,
            handleCertRemove, savePayConfig, goToPayTest, queryApplyStatus, openApplyDialog,
            cancelApply, showSignQrCode, copySignUrl, shouldShowSignButton, statusTitle, statusType,
            licenseFileList, idCardFrontFileList, idCardBackFileList, storeEntranceFileList,
            indoorFileList, miniProgramPicsFileList, mpPicsFileList, contactIdDocCopyFileList,
            contactIdDocCopyBackFileList, qualificationFileList, industryOptions, selectedIndustry,
            selectedIndustryInfo, needQualifications, qualificationsGuide, handleIndustryChange,
            bankList, bankLoading, needBankBranch, branchList, branchLoading, provinceList,
            provinceLoading, cityList, cityLoading, searchBanks, onBankChange, onAccountTypeChange,
            searchBranches, onBranchChange, fetchProvinces, onProvinceChange, onCityChange,
            onBankVisibleChange, reUploadImage, handleImageChange, handleImageRemove, resetApplyForm, submitApply
        }
    }
}
</script>

<style scoped lang="scss">
.pay-config {
    .page-header {
        margin-bottom: 24px;

        .page-title {
            font-size: 24px;
            font-weight: 600;
            color: #1a1a2e;
            margin-bottom: 4px;
        }

        .page-desc {
            font-size: 14px;
            color: #8c8c8c;
        }
    }

    .config-container {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    .config-card {
        border-radius: 16px;
        overflow: hidden;

        .card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .card-header-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 16px;
            font-weight: 500;
        }

        .config-content {
            .config-item {
                display: flex;
                padding: 12px 0;
                border-bottom: 1px solid #f0f0f0;

                &:last-child {
                    border-bottom: none;
                }

                .config-label {
                    width: 100px;
                    color: #8c8c8c;
                    font-size: 14px;
                }

                .config-value {
                    flex: 1;
                    color: #333;
                    font-size: 14px;
                    word-break: break-all;
                }
            }
        }
    }

    .form-tip {
        font-size: 12px;
        color: #999;
        margin-top: 4px;
        margin-left: 5px;
        line-height: 1.4;
    }

    .upload-tip {
        font-size: 12px;
        color: #8c8c8c;
        margin-top: 8px;
    }

    .cert-list {
        margin-top: 12px;

        .cert-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 12px;
            background: #f5f7fa;
            border-radius: 6px;
            margin-bottom: 8px;
            font-size: 13px;
        }
    }

    .apply-status {
        .status-detail {
            margin-top: 10px;

            p {
                margin: 5px 0;
                font-size: 14px;
                color: #606266;
            }

            .reject-reason {
                color: #f56c6c;
            }
        }

        .apply-actions {
            margin-top: 12px;
            text-align: right;
        }
    }

    .no-apply {
        padding: 20px 0;
    }

    .qr-container {
        text-align: center;

        .qr-image {
            width: 200px;
            height: 200px;
            margin: 0 auto;
            border: 1px solid #eee;
            border-radius: 8px;
        }

        .qr-tip {
            margin-top: 16px;
            font-size: 14px;
            color: #666;
        }

        .copy-link {
            margin-top: 12px;
        }
    }

    .qr-loading {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        padding: 40px;
        color: #666;
    }

    .success-tip {
        margin-top: 8px;
    }

    .upload-wrapper {
        display: inline-block;
        vertical-align: top;

        .upload-label {
            display: block;
            text-align: center;
            font-size: 12px;
            color: #999;
            margin-top: 5px;
        }
    }

    .uploaded-preview,
    .uploaded-item {
        .preview-image {
            position: relative;
            width: 148px;

            .preview-img {
                width: 148px;
                height: 148px;
                object-fit: cover;
                border-radius: 6px;
                border: 1px solid #dcdfe6;
            }

            .preview-actions {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                display: flex;
                justify-content: center;
                gap: 8px;
                padding: 8px;
                background: rgba(0, 0, 0, 0.6);
                border-radius: 0 0 6px 6px;
                opacity: 0;
                transition: opacity 0.3s;

                .el-button {
                    padding: 4px 8px;
                    font-size: 12px;
                }
            }

            &:hover .preview-actions {
                opacity: 1;
            }
        }
    }

    .id-card-uploads {
        display: flex;
        align-items: flex-start;

        .upload-item,
        .uploaded-item {
            display: inline-block;
            vertical-align: top;
        }
    }
}
</style>